// React imports
import { useContext, useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
// Hooks
import useUser from "../../hooks/useUser";
import { useStaffInfo } from "../../hooks/useStaffInfo";
import { useDraftOrder } from "../../state/draftOrderState";
import { useCheckAPI } from "../../hooks/useCheckAPI";
// Component imports
import { CustomStepper } from "../../components/CustomStepper";
import { BulkStaffSummary } from "./BulkStaffSummary";
import { BulkOrderItems } from "./BulkOrderItems";
import { StepChanger } from "./StepChanger";
import { DeliveryDetails } from "../DraftOrder/DeliveryDetails";
import { ShipmentOptions } from "../DraftOrder/ShipmentOptions";
import { SubmitOrder } from "../DraftOrder/SubmitOrder";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
import { useSidebar } from "../../contexts/SidebarContext";
// MUI imports
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

const steps = ["Create", "Address", "Options", "Submit"];

export function DraftOrderBulk() {
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const { collapse } = useSidebar();

  useEffect(() => {
    collapse();
  }, [collapse]);

  const [activeStep, setActiveStep] = useState(0);
  const { user } = useUser();
  const staffID = user.staffID;
  // Called unconditionally - a hook behind a ternary changes the hook order
  // between renders. The query itself is gated by `enabled` instead, and the
  // null result for "no staff ID" is preserved for consumers.
  const staffInfoResult = useStaffInfo(staffID, Boolean(staffID));
  const staffInfo = staffID ? staffInfoResult : null;
  const { id } = useParams({ strict: false });
  const getOrder = Boolean(id);

  const initOrderObj = {
    data: {
      filter: { noFilter: "" },
      order: {
        no: "",
        poNo: "",
        status: "",
        branch: "",
        requestedShippingDate: "",
        code: "",
        contact: "",
        name: "",
        address: "",
        address2: "",
        city: "",
        county: "",
        postCode: "",
        country: "",
        homeaddress: "",
        email: "",
        mobileNo: "",
        orderTotal: "",
      },
      staff: [],
    },
  };

  const [order, setOrder] = useState(initOrderObj);

  const {
    isError: isOrderError,
    error: orderError,
    data: orderData,
    isSuccess: isOrderSuccess,
  } = useDraftOrder(id, getOrder);

  const orderSuccessFunc = () => {
    const response = orderData?.data?.data?.data;
    if (response) {
      setOrder(response);
    }
  };

  const orderErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${orderData?.data?.status}: ${orderData?.data?.statusText}`,
      severity: "error",
    });

  useCheckAPI(
    isOrderSuccess,
    orderData,
    orderData?.data?.status,
    orderSuccessFunc,
    orderErrorFunc,
  );

  const [addressConfirmed, setAddressConfirmed] = useState(false);

  const shipmentDefault = {
    lines: [],
    subtotal: 0,
    total: 0,
    date: "",
    shipping: {
      index: "",
      agent: "",
      service: "",
      cost: 0,
    },
  };
  const [shipmentOne, setShipmentOne] = useState(shipmentDefault);
  const [shipmentTwo, setShipmentTwo] = useState(shipmentDefault);

  const [orderFinaliseInfo, setOrderFinaliseInfo] = useState({
    awaitingApproval: "false",
    approvalBy: "",
    minOrderValue: "0",
    maxOrderValue: "0",
    hasOverEntitlementItems: false,
  });

  return (
    <>
      <Box>
        <Typography variant="h5" sx={{ pb: 2 }}>
          Bulk Order
        </Typography>
        <CustomStepper steps={steps} activeStep={activeStep} />
        <Box sx={{ mt: 2 }}>
          {isOrderError && orderError && (
            <Typography color="error" sx={{ mb: 2 }}>
              Error loading order: {orderError.message}
            </Typography>
          )}
          {activeStep === 0 && (
            <Stack spacing={3}>
              {getOrder ? (
                <BulkOrderItems order={order} staffInfo={staffInfo} />
              ) : staffInfo ? (
                <BulkStaffSummary
                  staffData={staffInfo}
                  order={order}
                  onContinue={() => {}}
                />
              ) : (
                <Typography>
                  You do not have a linked Staff ID. Please contact support.
                </Typography>
              )}
            </Stack>
          )}
          {activeStep === 1 && (
            <DeliveryDetails
              order={order}
              onConfirmAddressChange={setAddressConfirmed}
            />
          )}
          {activeStep === 2 && (
            <ShipmentOptions
              order={order}
              shipmentDefault={shipmentDefault}
              shipmentOne={shipmentOne}
              setShipmentOne={setShipmentOne}
              shipmentTwo={shipmentTwo}
              setShipmentTwo={setShipmentTwo}
              setOrderFinaliseInfo={setOrderFinaliseInfo}
            />
          )}
          {activeStep >= 3 && (
            <SubmitOrder
              user={user}
              poNo={order.data.order.poNo}
              orderNo={order.data.order.no}
              setActiveStep={setActiveStep}
              shipmentOne={shipmentOne}
              shipmentTwo={shipmentTwo}
              orderFinaliseInfo={orderFinaliseInfo}
              orderType="bulk"
            />
          )}
        </Box>
        <StepChanger
          steps={steps}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          order={order}
          shipmentOne={shipmentOne}
          shipmentTwo={shipmentTwo}
          addressConfirmed={addressConfirmed}
        />
      </Box>
    </>
  );
}
